import type { EmployeeData } from '../../../../../../app/features/employee/emp-dashboard/emp-dashboard.type';
import ModuleGuard from '../../../../../../components/Guards/ModuleGuard';
import MODULES from '../../../../../../config/modules.config';
import { snakeToCamel } from '../../../../../../utils/string.utils';
import { useGetSalaryStructureQuery } from '@/app/features/employee/emp-payroll';

interface PayrollInfoProps {
  employee?: EmployeeData & {
    bank_name?: string;
    account_name?: string;
    account_number?: string;
    branch?: string;
    routing_number?: string;
    tin?: string;
    salary_breakdown?: any[];
  };
  loading?: boolean;
}

const PayrollInfo = ({ employee, loading }: PayrollInfoProps) => {
  // JTI: employees created before pay types exist are monthly.
  const payType = employee?.pay_basis || 'MONTHLY';
  const isDaily = payType === 'DAILY';

  const { data: salaryStructureData } = useGetSalaryStructureQuery(payType);
  const salary_breakdown = salaryStructureData?.data?.salary_structures;

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr,1fr] gap-6">
        <div className="bg-theme-slate-800 rounded-xl p-6">
          <div className="animate-pulse space-y-8">
            <div className="h-6 bg-theme-slate-700 rounded w-1/4"></div>
            <div className="space-y-6">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 bg-theme-slate-700 rounded w-1/4"></div>
                  <div className="h-5 bg-theme-slate-700 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-theme-slate-800 rounded-xl p-6 h-fit">
          <div className="animate-pulse space-y-8">
            <div className="h-6 bg-theme-slate-700 rounded w-1/4"></div>
            <div className="space-y-6">
              {[1, 2].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-4 bg-theme-slate-700 rounded w-1/4"></div>
                  <div className="h-5 bg-theme-slate-700 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.5fr,1fr] gap-6">
      {/* Bank Info */}
      <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10">
        <h6 className="text-2xl text-white font-black">Bank Info</h6>
        <div className="space-y-10">
          <InfoItem label="Bank Name" value={employee?.bank_name || '--'} />
          <InfoItem
            label="Account Name"
            value={employee?.account_name || '--'}
          />
          <InfoItem label="Branch" value={employee?.branch || '--'} />
          <InfoItem
            label="Account Number"
            value={employee?.account_number || '--'}
          />
          <InfoItem
            label="Routing Number"
            value={employee?.routing_number || '--'}
          />
        </div>
      </div>

      {/* Legal Info */}
      <div className="space-y-6">
        <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10 h-fit">
          <h6 className="text-2xl text-white font-black">Legal Info</h6>
          <div className="space-y-10">
            <InfoItem label="TIN" value={employee?.tin || '--'} />
            <InfoItem label="NID Number" value={employee?.nid || '--'} />
          </div>
        </div>

        <ModuleGuard moduleId={MODULES.EMPLOYEE_PAYROLL}>
          <div className="bg-theme-slate-800 rounded-xl p-6 space-y-10 h-fit">
            <h6 className="text-2xl text-white font-black">Salary Info</h6>
            <div className="space-y-10">
              <InfoItem label="Pay Type" value={isDaily ? 'Daily' : 'Monthly'} />
              {isDaily ? (
                <InfoItem
                  label="Daily Rate"
                  value={
                    employee?.daily_rate_bdt
                      ? `BDT ${Number(employee.daily_rate_bdt).toLocaleString()} / day`
                      : '--'
                  }
                />
              ) : (
                <InfoItem
                  label="Gross Salary"
                  value={
                    employee?.salary
                      ? `BDT ${employee.salary.toLocaleString()}`
                      : '--'
                  }
                />
              )}
              <div className="space-y-4">
                <h6 className="text-base text-gray-400 font-normal">
                  {isDaily ? 'Daily Breakdown' : 'Total Breakdown'}
                </h6>
                <div className="space-y-3">
                  {salary_breakdown?.map((s: any) => (
                    <div
                      key={s.id}
                      className="flex justify-between items-center bg-theme-slate-900/50 p-3 rounded-lg"
                    >
                      <span className="text-white text-base">
                        {snakeToCamel(s.component_name)}
                      </span>
                      <span className="text-teal-500 font-semibold">
                        {s.component_percentage}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </ModuleGuard>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="space-y-1">
      <h6 className="text-base text-gray-400 font-normal">{label}</h6>
      <h6 className="text-xl text-white font-medium">{value}</h6>
    </div>
  );
};

export default PayrollInfo;
